import React, { useState, useEffect } from "react";
import * as S from "./CaixinhaModalStyle";
import {
  FaTimes,
  FaCoins,
  FaCalendarAlt,
  FaPlus,
  FaTrash,
} from "react-icons/fa";
import { formatDate, formatCurrencyBRL } from "../ASSETS/assets";
import { goldenBoxAdminService } from "../../Services/goldenBoxAdminService";
import { useMessage } from "../../Context/MessageContext";

export default function CaixinhaModal({ data, onClose, onUpdate }) {
  const { showMessage } = useMessage();

  // Estados dos Sub-Modais
  const [showProfitForm, setShowProfitForm] = useState(false);
  const [showDateManager, setShowDateManager] = useState(false);

  // Estados de Input
  const [profitAmount, setProfitAmount] = useState("");
  const [newDateInput, setNewDateInput] = useState("");
  const [datesList, setDatesList] = useState([]); // Lista atual de datas
  const [datesToRemove, setDatesToRemove] = useState([]); // IDs ou Datas selecionadas para remover

  useEffect(() => {
    if (data) {
      // Reseta estados quando abre um novo modal
      setShowProfitForm(false);
      setShowDateManager(false);
      setProfitAmount("");

      // Carrega datas existentes
      if (data.nextWithdrawDates) {
        setDatesList([...data.nextWithdrawDates]);
      } else {
        setDatesList([]);
      }
    }
  }, [data]);

  if (!data) return null;

  // --- LÓGICA DE LUCRO ---
  const handleConfirmProfit = async () => {
    if (!profitAmount || parseFloat(profitAmount) <= 0) {
      alert("Digite um valor válido maior que zero.");
      return;
    }

    if (
      !window.confirm(
        `Tem certeza que deseja adicionar R$ ${profitAmount} de lucro nesta caixinha?`
      )
    ) {
      return;
    }

    try {
      await goldenBoxAdminService.addProfit(data.id, profitAmount);
      showMessage("Lucro adicionado com sucesso!", "success");
      setShowProfitForm(false);
      setProfitAmount("");
      if (onUpdate) onUpdate(); // Atualiza a tabela pai
      onClose(); // Fecha o modal (opcional, pode manter aberto se quiser ver o saldo atualizado, mas precisaria refetch do 'data')
    } catch (error) {
      console.error(error);
      showMessage("Erro ao adicionar lucro.", "error");
    }
  };

  // --- LÓGICA DE DATAS ---

  // 1. Adicionar Data
  const handleAddDate = async () => {
    if (!newDateInput) return alert("Selecione uma data.");

    if (
      !window.confirm(
        `Adicionar nova data aos saques?`
      )
    )
      return;

    // Adiciona a nova data ao array local (converter para ISO string para padronizar ou manter formato)
    // O backend espera List<DateTime>.
    const updatedDates = [...datesList, new Date(newDateInput).toISOString()];

    try {
      // Envia o array inteiro atualizado
      await goldenBoxAdminService.updateDates(data.id, updatedDates);

      showMessage("Data adicionada com sucesso!", "success");
      setDatesList(updatedDates); // Atualiza local
      setNewDateInput(""); // Limpa input
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error(error);
      showMessage("Erro ao adicionar data.", "error");
    }
  };

  // 2. Remover Datas (Salvar remoção)
  const handleSaveRemovals = async () => {
    if (datesToRemove.length === 0)
      return alert("Nenhuma data selecionada para remover.");

    if (
      !window.confirm(
        `Tem certeza que deseja remover ${datesToRemove.length} data(s)?`
      )
    )
      return;

    // Filtra mantendo apenas as que NÃO estão no array de remoção
    const updatedDates = datesList.filter((d) => !datesToRemove.includes(d));

    try {
      await goldenBoxAdminService.updateDates(data.id, updatedDates);

      showMessage("Datas removidas com sucesso!", "success");
      setDatesList(updatedDates);
      setDatesToRemove([]); // Limpa seleção
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error(error);
      showMessage("Erro ao remover datas.", "error");
    }
  };

  const toggleDateSelection = (dateStr) => {
    if (datesToRemove.includes(dateStr)) {
      setDatesToRemove((prev) => prev.filter((d) => d !== dateStr));
    } else {
      setDatesToRemove((prev) => [...prev, dateStr]);
    }
  };

  const getStatusText = (status) => {
    const statuses = [
      "Pendente",
      "Ativo",
      "Rejeitado",
      "Finalizado",
      "Cancelado",
    ]; // Ajuste conforme enum do back
    // Tenta pegar pelo índice numérico ou string
    if (typeof status === "number") return statuses[status] || status;

    const s = String(status).toUpperCase();
    if (s === "PENDING" || s === "1") return "Pendente";
    if (s === "ACTIVE" || s === "2") return "Ativo";
    if (s === "FINISHED" || s === "3") return "Finalizado";
    if (s === "REJECTED" || s === "4") return "Rejeitado";
    if (s === "CANCELED" || s === "5") return "Cancelado";
    return status;
  };

  return (
    <S.Overlay onClick={onClose}>
      <S.ModalContainer onClick={(e) => e.stopPropagation()}>
        <S.ModalHeader>
          <h2>
            Caixinha #{data.id} - {data.client?.name || "Cliente"}
          </h2>
          <button onClick={onClose}>
            <FaTimes />
          </button>
        </S.ModalHeader>

        <S.ModalBody>
          <S.InfoGrid>
            <S.InfoItem>
              <label>Status</label>
              <span className="highlight">{getStatusText(data.status)}</span>
            </S.InfoItem>
            <S.InfoItem>
              <label>Data Criação</label>
              <span>{formatDate(data.dateCreated)}</span>
            </S.InfoItem>
            <S.InfoItem>
              <label>Opção</label>
              <span>
                {data.goldenBoxOption?.name || `ID #${data.goldenBoxOptionId}`}
              </span>
            </S.InfoItem>
            <S.InfoItem>
              <label>Contrato</label>
              <span>#{data.contractId}</span>
            </S.InfoItem>
          </S.InfoGrid>

          <h3>Financeiro</h3>
          <S.InfoGrid>
            <S.InfoItem>
              <label>Acumulado Total</label>
              <span className="highlight">
                {formatCurrencyBRL(data.totalAccumulated || 0)}
              </span>
            </S.InfoItem>
            <S.InfoItem>
              <label>Disponível Saque</label>
              <span style={{ color: "#28a745" }}>
                {formatCurrencyBRL(data.currentAmountAvaliable || 0)}
              </span>
            </S.InfoItem>
            <S.InfoItem>
              <label>Bônus Mensal</label>
              <span>{data.monthlyBonus}%</span>
            </S.InfoItem>
            <S.InfoItem>
              <label>Rend. Final Est.</label>
              <span>{formatCurrencyBRL(data.finalIncome || 0)}</span>
            </S.InfoItem>
          </S.InfoGrid>

          <h3>Ferramentas Administrativas</h3>
          <S.ActionsSection>
            <S.ActionButton
              onClick={() => {
                setShowProfitForm(true);
                setShowDateManager(false);
              }}
            >
              <FaCoins />
              Adicionar Lucro
            </S.ActionButton>
            <S.ActionButton
              onClick={() => {
                setShowDateManager(true);
                setShowProfitForm(false);
              }}
            >
              <FaCalendarAlt />
              Gerenciar Datas Saque
            </S.ActionButton>
          </S.ActionsSection>

          {/* === FORMULÁRIO DE LUCRO === */}
          {showProfitForm && (
            <S.SubFormContainer>
              <h4>Adicionar Lucro Extra</h4>
              <S.InputGroup>
                <label>Valor em R$ (Use ponto para centavos)</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="Ex: 150.00"
                  value={profitAmount}
                  onChange={(e) => setProfitAmount(e.target.value)}
                />
              </S.InputGroup>
              <S.ButtonGroup>
                <button
                  className="cancel"
                  onClick={() => setShowProfitForm(false)}
                >
                  Cancelar
                </button>
                <button className="confirm" onClick={handleConfirmProfit}>
                  Confirmar
                </button>
              </S.ButtonGroup>
            </S.SubFormContainer>
          )}

          {/* === GERENCIADOR DE DATAS === */}
          {showDateManager && (
            <S.SubFormContainer>
              <h4>Gerenciar Datas de Saque</h4>

              {/* Adicionar */}
              <S.InputGroup>
                <label>Adicionar Nova Data</label>
                <div style={{ display: "flex", gap: "10px" }}>
                  <input
                    type="date"
                    value={newDateInput}
                    onChange={(e) => setNewDateInput(e.target.value)}
                  />
                  <button
                    style={{
                      padding: "0 20px",
                      background: "#e6aa3a",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontWeight: "bold",
                    }}
                    onClick={handleAddDate}
                  >
                    <FaPlus />
                  </button>
                </div>
              </S.InputGroup>

              {/* Listar e Remover */}
              <div style={{ marginTop: "20px" }}>
                <label
                  style={{
                    color: "#ccc",
                    fontSize: "12px",
                    display: "block",
                    marginBottom: "5px",
                  }}
                >
                  Datas Agendadas ({datesList.length}) - Selecione para remover
                </label>
                <S.DateList>
                  {datesList.length === 0 ? (
                    <div
                      style={{
                        padding: "10px",
                        color: "#666",
                        fontSize: "13px",
                      }}
                    >
                      Nenhuma data agendada.
                    </div>
                  ) : (
                    datesList.map((dateStr, index) => (
                      <S.DateItem key={index}>
                        <input
                          type="checkbox"
                          checked={datesToRemove.includes(dateStr)}
                          onChange={() => toggleDateSelection(dateStr)}
                        />
                        <label onClick={() => toggleDateSelection(dateStr)}>
                          {formatDate(dateStr)}
                        </label>
                      </S.DateItem>
                    ))
                  )}
                </S.DateList>

                {datesList.length > 0 && (
                  <S.ButtonGroup>
                    <button
                      className="cancel"
                      onClick={() => setShowDateManager(false)}
                    >
                      Fechar
                    </button>
                    <button
                      className="delete"
                      onClick={handleSaveRemovals}
                      disabled={datesToRemove.length === 0}
                      style={{ opacity: datesToRemove.length === 0 ? 0.5 : 1 }}
                    >
                      <FaTrash style={{ marginRight: "5px" }} />
                      Remover Selecionados
                    </button>
                  </S.ButtonGroup>
                )}
              </div>
            </S.SubFormContainer>
          )}
        </S.ModalBody>
      </S.ModalContainer>
    </S.Overlay>
  );
}
